import { PageSectionWorkspace } from '@/components/layout/PageSectionWorkspace';
import { useState } from 'react';
import { useUserPreferences } from '@/features/settings/UserPreferencesProvider';
import { Database, RefreshCw } from 'lucide-react';
import { Badge, Button, Card, EmptyState, ErrorState, PageHeader, Skeleton } from '@helios/ui';
import { hasPermission, PERMISSIONS } from '@helios/authz';
import { useAuth } from '@/features/auth/authContext';
import { sessionScopeKey, useSessionScope } from '@/hooks/useSessionScope';
import { useExpandSeedData, useSeedDataCommandCenter } from './seedData.hooks';
import type { SeedDomainKey } from './seedData.types';

export function SeedDataCommandCenterPage() {
  const { permissions } = useAuth();
  const { formatDateTime: formatDate } = useUserPreferences();
  const dashboardQuery = useSeedDataCommandCenter();
  const expandMutation = useExpandSeedData();
  const canAdmin = hasPermission(permissions, PERMISSIONS.PLATFORM_ADMIN);
  const { identity } = useSessionScope(PERMISSIONS.PLATFORM_ADMIN);
  const scope = JSON.stringify(sessionScopeKey(identity));
  const [selection, setSelection] = useState<{ scope: string; domains: SeedDomainKey[] }>({
    scope,
    domains: [],
  });
  const selectedDomains = selection.scope === scope ? selection.domains : [];
  const runError = expandMutation.error instanceof Error ? expandMutation.error.message : null;

  if (!canAdmin) {
    return (
      <EmptyState
        icon={<Database className="h-5 w-5" aria-hidden="true" />}
        title="Seed data access required"
        description={`Your role needs ${PERMISSIONS.PLATFORM_ADMIN} to view and expand enterprise seed coverage.`}
      />
    );
  }

  if (dashboardQuery.isLoading) {
    return <Skeleton className="h-96 w-full" />;
  }

  if (dashboardQuery.isError || !dashboardQuery.data) {
    return (
      <ErrorState
        title="Unable to load seed data"
        description={
          dashboardQuery.error instanceof Error
            ? dashboardQuery.error.message
            : 'Unknown seed data error'
        }
        action={
          <Button size="sm" variant="outline" onClick={() => dashboardQuery.refetch()}>
            Retry
          </Button>
        }
      />
    );
  }

  const data = dashboardQuery.data;

  const toggleDomain = (domainKey: SeedDomainKey) => {
    setSelection((current) => {
      const domains = current.scope === scope ? current.domains : [];
      return {
        scope,
        domains: domains.includes(domainKey)
          ? domains.filter((item) => item !== domainKey)
          : [...domains, domainKey],
      };
    });
  };

  return (
    <PageSectionWorkspace
      header={
        <PageHeader
          eyebrow="S32 · Enterprise Seed Data"
          title="Seed Data"
          description="Sample data coverage for demos and testing. Expanding a domain adds generated sample records to this workspace."
          actions={
            <Button size="sm" variant="outline" onClick={() => dashboardQuery.refetch()}>
              <RefreshCw className="mr-1.5 h-3.5 w-3.5" aria-hidden />
              Refresh
            </Button>
          }
        />
      }
      toolbar={
        <>
          <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
            <Card className="p-3">
              <p className="text-muted-foreground text-xs uppercase tracking-wide">Coverage</p>
              <p className="mt-1 text-2xl font-semibold tabular-nums">
                {data.summary.coveragePercent}%
              </p>
            </Card>
            <Card className="p-3">
              <p className="text-muted-foreground text-xs uppercase tracking-wide">Records</p>
              <p className="mt-1 text-2xl font-semibold tabular-nums">
                {data.summary.totalRecords.toLocaleString()} /{' '}
                {data.summary.totalTarget.toLocaleString()}
              </p>
            </Card>
            <Card className="p-3">
              <p className="text-muted-foreground text-xs uppercase tracking-wide">At target</p>
              <p className="mt-1 text-2xl font-semibold tabular-nums">
                {data.summary.domainsAtTarget}/{data.summary.domainCount}
              </p>
            </Card>
            <Card className="p-3">
              <p className="text-muted-foreground text-xs uppercase tracking-wide">
                Expansion runs
              </p>
              <p className="mt-1 text-2xl font-semibold tabular-nums">{data.summary.runCount}</p>
            </Card>
          </div>
        </>
      }
      sections={[
        {
          id: 'domain-coverage',
          label: 'Domain coverage',
          content: (
            <Card className="space-y-4 p-3">
              <div>
                <h2 className="hx-section-title text-sm font-semibold">Domain coverage</h2>
                <p className="text-muted-foreground text-sm">
                  Counts include existing workspace records. Select domains to add a batch of sample
                  records for testing.
                </p>
              </div>
              <div className="space-y-2">
                {data.domains.map((domain) => (
                  <label
                    key={domain.domainKey}
                    className="border-border flex cursor-pointer items-center justify-between gap-3 rounded-lg border px-3 py-2"
                  >
                    <span className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={selectedDomains.includes(domain.domainKey)}
                        onChange={() => toggleDomain(domain.domainKey)}
                      />
                      <span className="text-sm font-medium">{domain.label}</span>
                    </span>
                    <span className="text-muted-foreground text-xs tabular-nums">
                      {domain.currentCount.toLocaleString()} / {domain.targetCount.toLocaleString()}{' '}
                      ({domain.coveragePercent}%)
                    </span>
                  </label>
                ))}
              </div>
              {runError ? <p className="text-destructive text-sm">{runError}</p> : null}
              <Button
                size="sm"
                disabled={expandMutation.isPending || !data.permissions.canExpandSeedData}
                onClick={() => {
                  expandMutation.reset();
                  expandMutation.mutate({
                    domains: selectedDomains.length > 0 ? selectedDomains : undefined,
                    batchSize: 5,
                    scale: 'ENTERPRISE',
                  });
                }}
              >
                <Database className="mr-1.5 h-3.5 w-3.5" aria-hidden />
                {expandMutation.isPending
                  ? 'Expanding…'
                  : selectedDomains.length > 0
                    ? `Expand selected (${selectedDomains.length})`
                    : 'Expand all domains'}
              </Button>
            </Card>
          ),
        },
        {
          id: 'expansion-history',
          label: 'Expansion history',
          content: (
            <Card className="p-3">
              <h2 className="hx-section-title mb-3 text-sm font-semibold">Expansion history</h2>
              {data.runs.length === 0 ? (
                <EmptyState
                  icon={<Database className="h-5 w-5" aria-hidden="true" />}
                  title="No expansion runs yet"
                  description="Run a batch expansion to record audited SeedRun history."
                />
              ) : (
                <ul className="space-y-2">
                  {data.runs.map((run) => (
                    <li key={run.id} className="border-border rounded-lg border px-3 py-2">
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-sm font-medium">
                          {run.runNumber} · {run.scale}
                        </span>
                        <Badge variant="outline">{run.status}</Badge>
                      </div>
                      <p className="text-muted-foreground mt-1 text-xs">
                        {run.summary} · batch {run.batchSize} · {formatDate(run.createdAt)}
                      </p>
                      <p className="text-muted-foreground mt-1 text-xs">
                        Domains: {run.domainsCompleted.join(', ') || 'none created'}
                      </p>
                    </li>
                  ))}
                </ul>
              )}
            </Card>
          ),
        },
      ]}
    />
  );
}
